/**
 * currency-converter.js
 * ---------------------------------------------------------------------------
 * Purpose (read this before touching anything):
 *
 * LYLI PÉ's payment gateway is Azul, a Dominican-only processor. Shopify's native
 * multi-currency system (Markets) requires Shopify Payments to actually charge in a
 * second currency — with only Azul active, Shopify itself refuses to enable a second
 * presentment currency (confirmed directly against the Admin API: "The shop's payment
 * gateway does not support enabling more than one currency").
 *
 * So this script does NOT use Shopify Markets at all. It is a purely cosmetic,
 * client-side display layer:
 *   - It finds every price on the page (anything Shopify rendered with the `money`
 *     filter, which always wraps output in <span class="money">...</span> — product
 *     cards, product pages, cart, cart drawer, subtotals, discounts, unit prices, etc.)
 *   - It converts the real DOP amount to an approximate USD amount using a live
 *     exchange rate (fetched from a free public API, cached, refreshed periodically).
 *   - It lets the visitor toggle between "USD" (default) and "RD$" via the buttons
 *     rendered by snippets/currency-toggle.liquid.
 *
 * The cart and the actual Shopify checkout ALWAYS charge in DOP via Azul, no matter
 * what is displayed here — that part is a hard platform limitation, not something this
 * script can change. This only affects what the visitor SEES while browsing and in the
 * cart page/drawer.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var STORAGE_KEY_CURRENCY = 'lylipe_display_currency'; // 'USD' or 'DOP'
  var STORAGE_KEY_RATE = 'lylipe_usd_dop_rate';
  var STORAGE_KEY_RATE_TIME = 'lylipe_usd_dop_rate_time';
  var RATE_MAX_AGE_MS = 12 * 60 * 60 * 1000; // refresh the live rate at most every 12h
  // Used only the very first time this ever runs on a browser with no cached rate yet,
  // and if the live rate fetch also fails at that exact moment. Update occasionally if
  // you want, but in practice the live fetch below takes over within seconds.
  var FALLBACK_RATE = 62; // approx. DOP per 1 USD
  var RATE_API_URL = 'https://open.er-api.com/v6/latest/USD';
  // Displayed USD prices are rounded DOWN to the nearest multiple of this (no cents
  // shown), for "charm pricing" that always ends in 0 or 50 — e.g. a raw conversion
  // like $343.05 or $291.80 both show as a clean $300 (well, see ROUND_USD_DOWN below
  // for the actual floor step). Kept here for reference / possible future use with
  // roundToNearest(), but the price display itself now uses ROUND_USD_DOWN_TO below.
  var ROUND_USD_TO_NEAREST = 5;
  // Step used to floor (round DOWN, never up) the displayed USD price, so every price
  // ends in 0 or 50 — e.g. $275 -> $250, $375 -> $350, $345 -> $300. Change to 10 for
  // prices ending only in 0, or 1 to stop rounding and show whole dollars only.
  var ROUND_USD_DOWN_TO = 50;

  var state = {
    currency: 'USD',
    rate: null // DOP per 1 USD
  };

  function safeGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSet(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* ignore (private browsing, storage disabled, etc.) */
    }
  }

  function getStoredCurrency() {
    var value = safeGet(STORAGE_KEY_CURRENCY);
    return value === 'DOP' ? 'DOP' : 'USD';
  }

  function getCachedRate() {
    var rate = parseFloat(safeGet(STORAGE_KEY_RATE));
    var time = parseInt(safeGet(STORAGE_KEY_RATE_TIME), 10);
    if (rate && time && Date.now() - time < RATE_MAX_AGE_MS) {
      return rate;
    }
    return null;
  }

  function getLastKnownRate() {
    var rate = parseFloat(safeGet(STORAGE_KEY_RATE));
    return rate || FALLBACK_RATE;
  }

  function cacheRate(rate) {
    safeSet(STORAGE_KEY_RATE, String(rate));
    safeSet(STORAGE_KEY_RATE_TIME, String(Date.now()));
  }

  function refreshRate(onUpdated) {
    fetch(RATE_API_URL)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (data && data.rates && data.rates.DOP) {
          state.rate = data.rates.DOP;
          cacheRate(state.rate);
          if (typeof onUpdated === 'function') onUpdated();
        }
      })
      .catch(function () {
        /* keep whatever rate we already had (cached or fallback) */
      });
  }

  function parseMoney(text) {
    var cleaned = (text || '').replace(/[^0-9.,]/g, '');
    // Shopify's default {{amount}} token formats as "1,234.56" (comma thousands, dot decimal)
    cleaned = cleaned.replace(/,/g, '');
    var value = parseFloat(cleaned);
    return isNaN(value) ? null : value;
  }

  function roundToNearest(amount, step) {
    if (!step || step <= 0) return amount;
    return Math.round(amount / step) * step;
  }

  // Rounds DOWN (floors) to the nearest multiple of step — used for the "charm
  // pricing" display so a converted price never rounds up above what the DOP amount
  // actually converts to (e.g. 274.90 -> 250, not 300).
  function floorToNearest(amount, step) {
    if (!step || step <= 0) return amount;
    return Math.floor(amount / step) * step;
  }

  function formatUSD(amount) {
    var rounded = floorToNearest(amount, ROUND_USD_DOWN_TO);
    var formatted = rounded.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return '$' + formatted + ' USD';
  }

  function applyCurrency(node) {
    var original = node.getAttribute('data-original-money');
    if (original === null) return;

    if (state.currency === 'DOP') {
      node.textContent = original;
      return;
    }

    if (!state.rate) return;
    var dopAmount = parseMoney(original);
    if (dopAmount === null) return;
    node.textContent = formatUSD(dopAmount / state.rate);
  }

  function processNewNodes() {
    var nodes = document.querySelectorAll('.money:not([data-currency-processed])');
    nodes.forEach(function (node) {
      node.setAttribute('data-original-money', node.textContent);
      node.setAttribute('data-currency-processed', 'true');
      applyCurrency(node);
    });
  }

  function reapplyToAll() {
    var nodes = document.querySelectorAll('.money[data-original-money]');
    nodes.forEach(applyCurrency);
  }

  function observeDom() {
    var observer = new MutationObserver(function (mutations) {
      var hasAddedNodes = mutations.some(function (m) {
        return m.addedNodes && m.addedNodes.length > 0;
      });
      if (hasAddedNodes) processNewNodes();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function updateToggleUI() {
    document.querySelectorAll('[data-currency-toggle]').forEach(function (btn) {
      var isActive = btn.getAttribute('data-currency-value') === state.currency;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function setCurrency(value) {
    if (value !== 'USD' && value !== 'DOP') return;
    state.currency = value;
    safeSet(STORAGE_KEY_CURRENCY, value);
    reapplyToAll();
    updateToggleUI();
    document.dispatchEvent(
      new CustomEvent('currency:changed', { detail: { currency: value } })
    );
  }

  function bindToggleClicks() {
    document.body.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-currency-toggle]');
      if (!button) return;
      event.preventDefault();
      setCurrency(button.getAttribute('data-currency-value'));
    });
  }

  function init() {
    state.currency = getStoredCurrency();

    var cachedRate = getCachedRate();
    state.rate = cachedRate || getLastKnownRate();

    processNewNodes();
    updateToggleUI();
    observeDom();
    bindToggleClicks();

    if (!cachedRate) {
      refreshRate(function () {
        reapplyToAll();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exposed for debugging/manual use from the browser console if ever needed.
  window.LylipeCurrency = {
    setCurrency: setCurrency,
    getCurrency: function () {
      return state.currency;
    },
    getRate: function () {
      return state.rate;
    }
  };
})();
